import { useCallback, useContext, useEffect } from 'react';

import { capitalize } from 'App/Helpers';
import { LanguageContext } from 'App/Providers/games';
import { useAnalytics } from 'App/Services/Analytics';

const ANALYTICS_SURFACES = {
    desktop: { phase: 'PhaseGameDt', slug: 'game-dt' },
    mobile: { phase: 'PhaseGameMB', slug: 'game-mb' },
};

/**
 * Subscribes one handler to the `language.change` slot.
 *
 * @param {Function} addEventListener - Bridged Unity addEventListener.
 * @param {Function} removeEventListener - Bridged Unity removeEventListener.
 * @param {Function} handler
 */
const useLanguageChangeSlot = (addEventListener, removeEventListener, handler) => {
    useEffect(() => {
        addEventListener('language.change', handler);

        return () => removeEventListener('language.change', handler);
    }, [addEventListener, removeEventListener, handler]);
};

/**
 * Applies the locale Unity reports.
 *
 * Web landings use this. Desktop and mobile landings record the change with
 * {@link useLanguageChangeAnalytics} and leave the page locale as it is.
 *
 * @param {object} options
 * @param {Function} options.addEventListener - Bridged Unity addEventListener.
 * @param {Function} options.removeEventListener - Bridged Unity removeEventListener.
 */
export const useLanguageChange = ({ addEventListener, removeEventListener }) => {
    const { changeLanguage } = useContext(LanguageContext);

    const handleLanguageChange = useCallback(
        (gameLanguage) => {
            changeLanguage(gameLanguage.toLowerCase(), false);
        },
        [changeLanguage]
    );

    useLanguageChangeSlot(addEventListener, removeEventListener, handleLanguageChange);
};

/**
 * Records a language change from Unity. Does not change the page locale.
 *
 * @param {object} options
 * @param {'desktop'|'mobile'} options.surface - Landing surface, selects the existing event ids.
 * @param {string} options.pushStatus - Push permission status included in the events.
 * @param {Function} options.addEventListener - Bridged Unity addEventListener.
 * @param {Function} options.removeEventListener - Bridged Unity removeEventListener.
 */
export const useLanguageChangeAnalytics = ({
    surface,
    pushStatus,
    addEventListener,
    removeEventListener,
}) => {
    const { sendGameDesignEvent, sendGoogleBasicEvent } = useAnalytics();
    const surfaceIds = ANALYTICS_SURFACES[surface];

    const handleLanguageChange = useCallback(
        (gameLanguage) => {
            if (!surfaceIds) return;

            const updatedLocale = gameLanguage.toUpperCase();
            const updatedPushStatus = capitalize(pushStatus);

            sendGameDesignEvent(
                `Language:Change:${updatedLocale}:${surfaceIds.phase}:Push${updatedPushStatus}`
            );
            sendGoogleBasicEvent(
                'language',
                `change_${gameLanguage}_${surfaceIds.slug}_${pushStatus}`
            );
        },
        [surfaceIds, pushStatus, sendGameDesignEvent, sendGoogleBasicEvent]
    );

    useLanguageChangeSlot(addEventListener, removeEventListener, handleLanguageChange);

    if (!surfaceIds) {
        throw new Error(
            `useLanguageChangeAnalytics requires surface "desktop" or "mobile". Received "${surface}".`
        );
    }
};
